import { Shield, Scale, Phone, Mail, CheckCircle, Building2, Users, Briefcase, ArrowLeft, Globe } from 'lucide-react'
import SEOHead from '../../components/SEOHead'
import { Link } from 'react-router-dom'

export default function Weightmans() {
  const services = {
    organisations: [
      "Banking and FCA regulations",
      "Business and financial crime", 
      "Commercial Contracts",
      "Company law and corporate governance",
      "Competition law",
      "Construction",
      "Consumer law",
      "Corporate and Commercial Law",
      "Commercial litigation",
      "Criminal motor defence",
      "Data Protection and GDPR",
      "Debt recovery",
      "Dispute resolution and litigation",
      "Employment/HR",
      "Environmental",
      "Environmental, Social and Governance (ESG)",
      "Food safety",
      "Franchising",
      "Health and safety",
      "Healthcare regulation",
      "Immigration",
      "Information technology",
      "Inquests and coronial law",
      "Intellectual property",
      "Mergers and acquisitions",
      "Outsourcing",
      "Partnerships",
      "Pensions",
      "Private equity and venture capital",
      "Public inquiries",
      "Real estate",
      "Regulatory",
      "Restructuring and insolvency",
      "Tax advice",
      "Trading standards",
      "Transport regulation"
    ],
    individuals: [
      "Commercial law",
      "Commercial litigation",
      "Share sale and purchase",
      "Disputed wills, trusts & estates",
      "Employment law",
      "Family law",
      "Private wealth",
      "Real estate law",
      "Regulatory law",
      "Residential property",
      "Senior Executive health check"
    ],
    insurance: [
      "Casualty (bodily injury)",
      "Catastrophic Injury",
      "Counter fraud",
      "Cross-border and travel",
      "Defending winter sports accidents",
      "Directors and officers",
      "Medical malpractice",
      "Mental health in the workplace",
      "Motor",
      "Occupational disease",
      "Product liability",
      "Professional risk",
      "Property damage",
      "Sport",
      "Technology and cyber"
    ],
    products: [
      "Auxilium",
      "Calculate costs",
      "Calculate – credit hire",
      "Calculate – holiday pay",
      "Consumer finance agreements",
      "Contract reviewer",
      "Disability tools",
      "Economic crime",
      "Enable – litigation funding",
      "PREDiCT",
      "Remedy",
      "Resolve",
      "Sexual misconduct complaints",
      "Industrial relations",
      "Transport manager comply"
    ]
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title="C9 Legal Assist powered by Weightmans - Legal Support for Transport Operators"
        description="C9 Recruitment and Weightmans partnership providing PSV/HGV operators with comprehensive legal support. Free initial consultation and preferential rates."
        keywords="C9 Legal Assist, Weightmans, transport legal support, PSV legal services, HGV legal services, transport lawyers"
        canonical="/partners/weightmans"
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-24 md:py-32">
          <div className="max-w-4xl mx-auto">
            {/* Back to Partners link */}
            <Link 
              to="/partners" 
              className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Partners
            </Link>
            
            <div className="text-center">
              <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-6 backdrop-blur-sm">
                Strategic Legal Partnership
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                C9 Legal Assist
                <span className="block text-3xl md:text-4xl mt-4 font-normal">
                  powered by{" "}
                  <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                    Weightmans
                  </span>
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
                Supporting your business and protecting your interests with comprehensive 
                legal services tailored for the transport industry
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Overview */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-c9-blue mb-6">
                A Partnership Built on{" "}
                <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                  Industry Expertise
                </span>
              </h2>
              
              <p className="text-gray-700 leading-relaxed text-lg">
                C9 Recruitment and Weightmans are excited to announce their strategic partnership 
                to provide PSV/HGV operators with legal support across all of their operational 
                requirements.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                The collaboration draws upon the extensive reach and trusted brand of C9 Recruitment 
                within the transport industry, along with benchmark legal strength offered by Weightmans 
                as a full-service law firm, with sector specialism in transport.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                Weightmans are a Top 40 law firm, with deep sector expertise in transport and logistics. 
                Weightmans transport lawyers advise and represent operators across their commercial 
                operations, including commercial, corporate M&A, employment, data protection, 
                cyber-security, real estate and transport regulatory matters.
              </p>
            </div>

            <div className="space-y-6">
              {/* Benefits Box */}
              <div className="bg-gradient-to-br from-c9-orange/5 to-orange-50 rounded-2xl p-8 border border-c9-orange/20">
                <h3 className="text-2xl font-bold text-c9-blue mb-6 flex items-center">
                  <CheckCircle className="h-7 w-7 text-c9-orange mr-3" />
                  What C9 Legal Assist Offers
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-c9-orange mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Free initial consultation</strong> with Weightmans 
                      on any legal matter affecting your operations
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-c9-orange mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Preferential rates</strong> for ongoing support 
                      and legal services
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-c9-orange mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Cost-effective solutions</strong> to navigate 
                      complex legal challenges with confidence
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-c9-orange mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Industry-specific expertise</strong> tailored 
                      to transport and logistics operations
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-c9-orange mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Dedicated support team</strong> familiar with 
                      the unique challenges of the transport sector
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Expert & Contact Boxes - Aligned Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Expert Box */}
            <div className="bg-gradient-to-r from-c9-blue to-c9-blue-alt rounded-xl p-8 text-white h-full flex flex-col">
              <h3 className="font-semibold mb-4 text-xl flex items-center">
                <Shield className="h-6 w-6 mr-3" />
                Led by Transport Regulatory Expert
              </h3>
              <p className="text-gray-200 leading-relaxed flex-grow">
                This initiative is spearheaded by Elliott Kenton, a Transport Regulatory lawyer at 
                Weightmans. Elliott specialises in providing operators with risk advisory services 
                and support, including responding to on-road or off-road incidents, health and safety 
                investigations and representing operators before the Traffic Commissioners.
              </p>
            </div>

            {/* Contact Box */}
            <div className="bg-gradient-to-r from-c9-orange to-orange-400 rounded-xl p-8 text-white shadow-xl h-full flex flex-col">
              <h3 className="text-xl font-bold mb-6">Access the Service</h3>
              <div className="space-y-4 mb-6 flex-grow">
                <a 
                  href="mailto:C9legalassist@weightmans.com" 
                  className="flex items-center space-x-3 text-white hover:text-gray-200 transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <span className="text-lg">C9legalassist@weightmans.com</span>
                </a>
                <a 
                  href="tel:03301796345" 
                  className="flex items-center space-x-3 text-white hover:text-gray-200 transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Phone className="h-5 w-5" />
                  </div>
                  <span className="text-lg">0330 1796 345</span>
                </a>
              </div>
              <p className="text-gray-100 text-sm">
                Contact us to access a member of the Weightmans transport team
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Comprehensive{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Legal Services
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Full-service legal support across all aspects of your transport operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* For Organisations */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-c9-blue to-c9-blue-alt rounded-xl flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-c9-blue text-lg">For Organisations</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 max-h-96 overflow-y-auto pr-2">
                {services.organisations.map((service, index) => (
                  <div key={index} className="flex items-start group">
                    <span className="text-c9-orange mr-2 group-hover:text-c9-orange-alt transition-colors">•</span>
                    <span className="group-hover:text-gray-800 transition-colors">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* For Individuals */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-c9-blue to-c9-blue-alt rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-c9-blue text-lg">For Individuals</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 max-h-96 overflow-y-auto pr-2">
                {services.individuals.map((service, index) => (
                  <div key={index} className="flex items-start group">
                    <span className="text-c9-orange mr-2 group-hover:text-c9-orange-alt transition-colors">•</span>
                    <span className="group-hover:text-gray-800 transition-colors">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Insurance */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-c9-blue to-c9-blue-alt rounded-xl flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-c9-blue text-lg">Insurance</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 max-h-96 overflow-y-auto pr-2">
                {services.insurance.map((service, index) => (
                  <div key={index} className="flex items-start group">
                    <span className="text-c9-orange mr-2 group-hover:text-c9-orange-alt transition-colors">•</span>
                    <span className="group-hover:text-gray-800 transition-colors">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Products */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-c9-blue to-c9-blue-alt rounded-xl flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-c9-blue text-lg">Products</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 max-h-96 overflow-y-auto pr-2">
                {services.products.map((product, index) => (
                  <div key={index} className="flex items-start group">
                    <span className="text-c9-orange mr-2 group-hover:text-c9-orange-alt transition-colors">•</span>
                    <span className="group-hover:text-gray-800 transition-colors">{product}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Why Choose{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                C9 Legal Assist
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Scale className="h-8 w-8" />,
                title: "Transport Expertise",
                description: "Specialist lawyers who understand the unique challenges and regulations of the transport industry."
              },
              {
                icon: <Globe className="h-8 w-8" />,
                title: "Full Service Coverage",
                description: "Comprehensive legal support covering all aspects of your business operations and compliance."
              },
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Risk Mitigation",
                description: "Proactive legal guidance to help prevent issues before they arise and protect your business."
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-c9-blue mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-c9-orange to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
        
        <div className="relative container-full text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Get Your Free Legal Consultation Today
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Take advantage of our partnership with Weightmans and access expert legal advice 
            tailored to your transport operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:C9legalassist@weightmans.com" 
              className="bg-white text-c9-orange px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email C9 Legal Assist
            </a>
            <a 
              href="tel:03301796345" 
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-c9-orange transition-all duration-300 inline-flex items-center justify-center"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call 0330 1796 345
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
